import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Edit2, Plus, Save, Search, Tag, Trash2, X } from 'lucide-react';
import { useNotify } from '../components/NotificationProvider';
import {
  actualizarCategoria,
  crearCategoria,
  eliminarCategoria,
  obtenerCategorias
} from '../api/categorias';

const normalize = (value) => String(value || '').trim().toLowerCase();
const CATS_PER_PAGE = 5;

const AdminCategories = () => {
  const { success: notifySuccess, error: notifyError, info: notifyInfo } = useNotify();
  const [categorias, setCategorias] = useState([]);
  const [nombre, setNombre] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);

  const cargarCategorias = async () => {
    try {
      setLoading(true);
      const data = await obtenerCategorias();
      setCategorias(Array.isArray(data) ? data : []);
    } catch (err) {
      notifyError(err.message || 'Error al cargar categorias', { duration: 4500 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarCategorias();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  const categoriasFiltradas = useMemo(() => {
    const q = normalize(searchTerm);
    if (!q) return categorias;
    return categorias.filter((cat) => normalize(cat.Nombre).includes(q));
  }, [categorias, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(categoriasFiltradas.length / CATS_PER_PAGE));
  const safePage = Math.min(page, totalPages);
  const startIndex = (safePage - 1) * CATS_PER_PAGE;
  const visibleCategorias = categoriasFiltradas.slice(startIndex, startIndex + CATS_PER_PAGE);

  const handleCreate = async (e) => {
    e.preventDefault();
    const nextName = normalize(nombre);
    if (!nextName || saving) return;

    try {
      setSaving(true);
      await crearCategoria(nextName);
      setNombre('');
      await cargarCategorias();
      notifySuccess('Categoria creada', { duration: 3000 });
    } catch (err) {
      notifyError(err.message || 'No se pudo crear la categoria', { duration: 4500 });
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (categoria) => {
    setEditingId(categoria.Id);
    setEditingName(categoria.Nombre || '');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingName('');
  };

  const handleUpdate = async (id) => {
    const nextName = normalize(editingName);
    if (!nextName || saving) return;

    try {
      setSaving(true);
      await actualizarCategoria(id, nextName);
      cancelEdit();
      await cargarCategorias();
      notifySuccess('Categoria actualizada', { duration: 3000 });
    } catch (err) {
      notifyError(err.message || 'No se pudo actualizar la categoria', { duration: 4500 });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (categoria) => {
    const usados = Number(categoria.ProductosAsociados || 0);
    if (usados > 0) {
      notifyInfo('No se puede eliminar: tiene productos asociados', { duration: 4000 });
      return;
    }

    try {
      setSaving(true);
      await eliminarCategoria(categoria.Id);
      await cargarCategorias();
      notifySuccess('Categoria eliminada', { duration: 3000 });
    } catch (err) {
      notifyError(err.message || 'No se pudo eliminar la categoria', { duration: 4500 });
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.45 }}
    >
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-1">
            Mantenedor de Categorias
          </h1>
          <p className="text-sm text-gray-600">Administra las categorias del catalogo</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-4">
        <form
          onSubmit={handleCreate}
          className="bg-white/90 rounded-xl shadow-lg border border-pink-100 p-4 h-fit"
        >
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-pink-100 text-pink-600 flex items-center justify-center">
              <Tag className="w-4 h-4" />
            </div>
            <h2 className="text-base font-bold text-gray-900">Nueva categoria</h2>
          </div>

          <label className="block text-xs font-semibold text-gray-700 mb-1">
            Nombre
          </label>
          <input
            type="text"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border-2 border-gray-200 focus:border-pink-500 focus:outline-none"
            maxLength={50}
            required
          />

          <button
            type="submit"
            disabled={saving || !normalize(nombre)}
            className="mt-3 w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg font-bold text-sm text-white bg-gradient-to-r from-pink-500 to-purple-600 disabled:opacity-50"
          >
            <Plus className="w-4 h-4" />
            Agregar categoria
          </button>
        </form>

        <div className="bg-white/90 rounded-xl shadow-lg border border-pink-100 p-4">
          <div className="flex flex-col md:flex-row md:items-center gap-3 mb-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar categoria..."
                className="w-full pl-9 pr-3 py-2 rounded-lg border-2 border-gray-200 focus:border-pink-500 focus:outline-none text-sm"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-pink-100 text-left text-purple-700">
                  <th className="py-3 px-3 font-bold">Categoria</th>
                  <th className="py-3 px-3 font-bold">Productos</th>
                  <th className="py-3 px-3 font-bold text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={3} className="py-8 text-center text-gray-500">
                      Cargando...
                    </td>
                  </tr>
                ) : categoriasFiltradas.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="py-8 text-center text-gray-500">
                      Sin categorias
                    </td>
                  </tr>
                ) : (
                  visibleCategorias.map((categoria) => {
                    const isEditing = editingId === categoria.Id;
                    const usados = Number(categoria.ProductosAsociados || 0);

                    return (
                      <tr key={categoria.Id} className="border-b border-gray-100">
                        <td className="py-2 px-3">
                          {isEditing ? (
                            <input
                              type="text"
                              value={editingName}
                              onChange={(e) => setEditingName(e.target.value)}
                              className="w-full px-3 py-1.5 rounded-lg border border-pink-200 focus:border-pink-500 focus:outline-none"
                              maxLength={50}
                              autoFocus
                            />
                          ) : (
                            <span className="font-semibold text-gray-900">
                              {categoria.Nombre}
                            </span>
                          )}
                        </td>
                        <td className="py-2 px-3 text-gray-600">{usados}</td>
                        <td className="py-2 px-3">
                          <div className="flex items-center justify-end gap-2">
                            {isEditing ? (
                              <>
                                <button
                                  type="button"
                                  onClick={() => handleUpdate(categoria.Id)}
                                  disabled={saving || !normalize(editingName)}
                                  className="p-1.5 rounded-lg bg-green-100 text-green-700 hover:bg-green-200 disabled:opacity-50"
                                  title="Guardar"
                                >
                                  <Save className="w-4 h-4" />
                                </button>
                                <button
                                  type="button"
                                  onClick={cancelEdit}
                                  className="p-1.5 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200"
                                  title="Cancelar"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  type="button"
                                  onClick={() => startEdit(categoria)}
                                  className="p-1.5 rounded-lg bg-blue-100 text-blue-700 hover:bg-blue-200"
                                  title="Editar"
                                >
                                  <Edit2 className="w-4 h-4" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDelete(categoria)}
                                  disabled={saving || usados > 0}
                                  className="p-1.5 rounded-lg bg-red-100 text-red-700 hover:bg-red-200 disabled:opacity-40"
                                  title="Eliminar"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {categoriasFiltradas.length > 0 && (
            <div className="flex items-center justify-between border-t border-pink-100 px-3 py-3 mt-2">
              <p className="text-xs text-gray-600">
                {startIndex + 1}-{Math.min(startIndex + CATS_PER_PAGE, categoriasFiltradas.length)} de {categoriasFiltradas.length}
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={safePage === 1}
                  className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 text-gray-700 hover:bg-purple-50 disabled:opacity-40"
                  aria-label="Pagina anterior"
                >
                  &lt;
                </button>
                <span className="min-w-16 text-center text-xs font-semibold text-gray-700">
                  {safePage} / {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={safePage === totalPages}
                  className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 text-gray-700 hover:bg-purple-50 disabled:opacity-40"
                  aria-label="Pagina siguiente"
                >
                  &gt;
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default AdminCategories;
